import ODataStore from 'devextreme/data/odata/store'
import DataSource from 'devextreme/data/data_source'
import { useRef } from 'react'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import type { contatto_aziendale, intervento, task, user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useTokenRefresh, AzureUserInfo } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import DateColumn from '@/components/date-column/DateColumn'
import { isUserRoleAllowed } from '@/routes/utils'
import { useScreenSize } from '@/themes/media-query'
import {
  Column,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Export,
  Item,
  Paging,
  StateStoring,
  Toolbar,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import { ColumnButtonClickEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import notify from 'devextreme/ui/notify'
import { Link, useNavigate } from 'react-router-dom'
import { ClickEvent } from 'devextreme/ui/button'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
const TaskEditorFormInterventionsGrid = (props: { task: task; userInfo: AzureUserInfo | undefined }) => {
  const { task, userInfo } = props
  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const token = useTokenRefresh()
  const navigate = useNavigate()

  const computeCustRifValue = (rowData: intervento) => {
    let custRifs = ''
    rowData.riferimenti_cliente?.map((custRif: contatto_aziendale) => {
      custRifs = `${custRifs + (custRifs.length > 0 ? ' ' : '')}${custRif.fullname}`
    })
    return custRifs.trim()
  }

  const cellRiferimentiRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    return (
      <>
        {cellData.data.riferimenti_cliente?.map((contatto: contatto_aziendale, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
              <span>{contatto.fullname}</span>
            </div>
          )
        })}
      </>
    )
  }

  const computeTechniciansValue = (rowData: intervento) => {
    let techs = ''
    rowData.impiegati?.map((tech: user) => {
      techs = `${techs + (techs.length > 0 ? ' - ' : '')}${tech.fullname}`
    })
    return techs.trim()
  }

  const ExportDataGridToExcel = (component: any) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('TaskInterventionsGrid')
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'TaskInterventions.xlsx')
      })
    })
  }

  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const interventionsTaskDataSource = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/intervento`,
      key: 'id',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [['task.id', task.id]],
    expand: ['impiegati', 'riferimenti_cliente'],
  })

  return (
    <>
      <DataGrid
        id={'task-interventions-datagrid'}
        dataSource={interventionsTaskDataSource}
        ref={gridRef}
        noDataText="Nessun intervento associato al task"
        keyExpr={'id'}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`../../interventions/${e.data.id}`)
        }}
        onExporting={onExporting}
      >
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey={'task-interventions-datagrid'}
          savingTimeout={50}
        />
        <Paging defaultPageSize={8} />
        <Export enabled={true} />
        <Column
          dataField={'ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: intervento = cellData.row.data
            return (
              <ObjectCodeLink
                url={`../../interventions/${data.id}`}
                text={data.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          caption="CODICE"
        ></Column>
        {DateColumn({
          dataField: 'data',
          caption: 'DATA',
          format: 'dd/MM/yyyy',
        })}
        <Column
          dataField={'impiegati'}
          caption="TECNICI"
          calculateCellValue={computeTechniciansValue}
          allowSorting={false}
        ></Column>
        <Column
          dataField={'riferimenti_cliente'}
          caption="RIFERIMENTI"
          cellRender={cellRiferimentiRender}
          calculateCellValue={computeCustRifValue}
          allowSorting={false}
        ></Column>
        <Column dataField={'durata_intervento'} dataType="number" caption="DURATA"></Column>
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
          />
        </Toolbar>
      </DataGrid>
    </>
  )
}

export default TaskEditorFormInterventionsGrid
